import { ChevronDown } from "lucide-react";
import * as React from "react";

import { cn } from "../../../utils";

export type TSelectOption = {
  label: string;
  value: string;
};

export type TSelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  options: TSelectOption[];
  /** Placeholder option rendered as the empty ("") value. */
  placeholder?: string;
  onValueChange?: (value: string) => void;
};

/**
 * Accessible native select styled to match the UI kit. Native is intentional:
 * it is fully keyboard-operable and screen-reader friendly out of the box, and
 * is the right primitive for the grid's simple single-select filters.
 */
const Select = React.forwardRef<HTMLSelectElement, TSelectProps>(
  ({ className, options, placeholder, onValueChange, onChange, ...props }, ref) => (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          "h-9 w-full appearance-none rounded-md border border-input bg-background pl-3 pr-8 text-sm text-foreground ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        onChange={(event) => {
          onChange?.(event);
          onValueChange?.(event.target.value);
        }}
        {...props}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
          >
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
    </div>
  )
);
Select.displayName = "Select";

export { Select };
